import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Sparkles, ArrowRight, ShoppingBag } from 'lucide-react';

const getApiBase = () => {
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')
  ) {
    return 'http://localhost:5000';
  }
  return (
    process.env.REACT_APP_API_BASE_URL ||
    'https://api.vanyacollectionsrajahmundry.com'
  );
};
const API_BASE = getApiBase();

const AuthPage = () => {
  const [isSignUp, setIsSignUp] = useState(false); // default to Sign In
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    identifier: '', // email or phone for login
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState({
    show: false,
    isSignUp: false,
    name: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (isSignUp) {
      if (!formData.fullName.trim()) {
        return setError('Please enter your full name');
      }

      const cleanPhone = formData.phone.replace(/\D/g, '').slice(-10);
      if (cleanPhone.length !== 10) {
        return setError('Please enter a valid 10-digit mobile number');
      }

      if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
        return setError('Please enter a valid email address');
      }

      if (formData.password.length < 6) {
        return setError('Password must be at least 6 characters long');
      }

      if (formData.password !== formData.confirmPassword) {
        return setError('Passwords do not match');
      }
    } else {
      const loginId = (formData.identifier || formData.email || '').trim();
      if (!loginId) {
        return setError('Please enter your email address or 10-digit mobile number');
      }
      if (!formData.password) {
        return setError('Please enter your password');
      }
    }

    setLoading(true);

    try {
      const endpoint = isSignUp ? 'register' : 'login';
      const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '').slice(-10) : '';

      const payload = isSignUp
        ? {
            fullName: formData.fullName.trim(),
            phone: cleanPhone,
            mobile: cleanPhone,
            email: formData.email.trim().toLowerCase(),
            password: formData.password,
          }
        : {
            identifier: (formData.identifier || formData.email || '').trim(),
            email: (formData.identifier || formData.email || '').trim(),
            password: formData.password,
          };

      const res = await fetch(`${API_BASE}/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      setLoading(false);

      if (!res.ok) {
        setError(data.message || 'Authentication failed. Please check your details.');
      } else {
        const successMsg = data.message || (isSignUp ? 'Registered Successfully!' : 'Login Successfully!');
        setSuccess(successMsg);

        // Store user in localStorage
        const userToStore = {
          ...data.user,
          phone: data.user?.phone || (isSignUp ? cleanPhone : ''),
        };
        localStorage.setItem('user', JSON.stringify(userToStore));
        window.dispatchEvent(new Event('storage'));
        window.dispatchEvent(new Event('vanya_auth_change'));

        // Clear form
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          identifier: '',
          password: '',
          confirmPassword: '',
        });

        // Trigger Royal Success Modal Popup
        setSuccessModal({
          show: true,
          isSignUp: isSignUp,
          name: userToStore.fullName || userToStore.name || '',
        });

        // Auto redirect to shop page after 2 seconds
        setTimeout(() => {
          navigate('/shop');
        }, 2000);
      }
    } catch (err) {
      setLoading(false);
      setError('Server error. Please check your connection and try again.');
      console.error(err);
    }
  };

  return (
    <div style={styles.pageWrapper}>
      <style>{hoverEffects}</style>
      <main style={styles.mainContainer}>
        <h1 style={styles.title}>Vanya Collections</h1>
        <p style={styles.subtitle}>
          {isSignUp ? 'Begin your royal journey with us' : 'Access your royal account'}
        </p>

        <div style={styles.formCard}>
          {/* Tab Switcher */}
          <div style={styles.tabContainer}>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(false);
                setError('');
                setSuccess('');
              }}
              style={{
                ...styles.tabBtn,
                background: !isSignUp ? '#D4AF37' : 'transparent',
                color: !isSignUp ? '#513262' : '#D4AF37',
                boxShadow: !isSignUp ? '0 4px 12px rgba(212, 175, 55, 0.3)' : 'none',
              }}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(true);
                setError('');
                setSuccess('');
              }}
              style={{
                ...styles.tabBtn,
                background: isSignUp ? '#D4AF37' : 'transparent',
                color: isSignUp ? '#513262' : '#D4AF37',
                boxShadow: isSignUp ? '0 4px 12px rgba(212, 175, 55, 0.3)' : 'none',
              }}
            >
              Sign Up
            </button>
          </div>

          {/* Form */}
          <form style={styles.form} onSubmit={handleSubmit}>
            {isSignUp ? (
              <>
                {/* FULL NAME */}
                <div style={styles.inputGroup}>
                  <span style={styles.icon}>👤</span>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    style={styles.input}
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* MOBILE NUMBER */}
                <div style={styles.inputGroup}>
                  <span style={styles.icon}>📱</span>
                  <input
                    type="tel"
                    name="phone"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit Mobile Number"
                    style={styles.input}
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* EMAIL ADDRESS */}
                <div style={styles.inputGroup}>
                  <span style={styles.icon}>✉️</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="off"
                    placeholder="Email Address"
                    style={styles.input}
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </>
            ) : (
              /* SIGN IN: EMAIL OR MOBILE NUMBER */
              <div style={styles.inputGroup}>
                <span style={styles.icon}>📱 / ✉️</span>
                <input
                  type="text"
                  name="identifier"
                  autoComplete="username"
                  placeholder="Mobile Number or Email Address"
                  style={styles.input}
                  value={formData.identifier}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {/* PASSWORD */}
            <div style={styles.inputGroup}>
              <span style={styles.icon}>🔒</span>
              <input
                type="password"
                name="password"
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                placeholder="Password"
                style={styles.input}
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>

            {/* CONFIRM PASSWORD (ONLY IN SIGN UP) */}
            {isSignUp && (
              <div style={styles.inputGroup}>
                <span style={styles.icon}>🔐</span>
                <input
                  type="password"
                  name="confirmPassword"
                  autoComplete="new-password"
                  placeholder="Confirm Password"
                  style={styles.input}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {error && <p style={styles.errorText}>{error}</p>}
            {success && <p style={styles.successText}>{success}</p>}

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitBtn,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              className="submit-btn-hover"
            >
              {loading
                ? 'Processing...'
                : isSignUp
                ? 'Create Royal Account'
                : 'Sign In to Account'}
            </button>
          </form>

          <div style={styles.divider}>Experience timeless heritage & luxury</div>
        </div>
      </main>

      {/* ROYAL SUCCESS POPUP MODAL */}
      {successModal.show && (
        <div style={styles.modalOverlay}>
          <div style={styles.successModalCard}>
            <div style={styles.modalGlow} />

            <div style={styles.iconCircle}>
              <CheckCircle2 size={42} color="#D4AF37" />
            </div>

            <div style={styles.royalBadge}>
              <Sparkles size={13} color="#fbd349" />
              <span>{successModal.isSignUp ? 'ACCOUNT CREATED' : 'AUTHENTICATION SUCCESS'}</span>
            </div>

            <h2 style={styles.modalHeading}>
              {successModal.isSignUp ? 'Registered Successfully!' : 'Login Successfully!'}
            </h2>

            <p style={styles.modalMessage}>
              {successModal.isSignUp
                ? `Welcome to Vanya Collections${successModal.name ? `, ${successModal.name}` : ''}! Your royal account has been created.`
                : `Welcome back${successModal.name ? `, ${successModal.name}` : ''}! You are logged in successfully.`}
            </p>

            <div style={styles.redirectNotice}>
              <span className="auth-pulse-dot" />
              <span>Redirecting you to the Shop page...</span>
            </div>

            <button
              type="button"
              onClick={() => navigate('/shop')}
              style={styles.modalCtaBtn}
              className="submit-btn-hover"
            >
              <ShoppingBag size={18} />
              <span>Continue to Shop</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ================= CSS-in-JS =================
const styles = {
  pageWrapper: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at center, #633c78 0%, #513262 100%)',
    fontFamily: '"Playfair Display", serif, sans-serif',
    color: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  mainContainer: {
    textAlign: 'center',
    width: '100%',
    maxWidth: '460px',
  },
  title: {
    fontSize: '38px',
    marginBottom: '6px',
    color: '#D4AF37',
    fontWeight: '750',
    letterSpacing: '1px',
  },
  subtitle: {
    fontSize: '15px',
    color: '#f3e5ab',
    marginBottom: '30px',
    letterSpacing: '0.5px',
  },
  formCard: {
    background: 'rgba(81, 50, 98, 0.75)',
    backdropFilter: 'blur(12px)',
    padding: '40px 35px',
    borderRadius: '24px',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    boxShadow: '0 15px 40px rgba(45, 25, 55, 0.6)',
    textAlign: 'left',
  },
  tabContainer: {
    display: 'flex',
    background: 'rgba(50, 30, 62, 0.6)',
    borderRadius: '30px',
    marginBottom: '30px',
    padding: '5px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
  },
  tabBtn: {
    flex: 1,
    padding: '12px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    transition: 'all 0.3s ease',
    borderRadius: '25px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(65, 38, 80, 0.6)',
    border: '1px solid rgba(212, 175, 55, 0.35)',
    borderRadius: '30px',
    padding: '0 20px',
    transition: 'all 0.3s ease',
  },
  input: {
    flex: 1,
    background: 'none',
    border: 'none',
    padding: '15px 10px',
    color: '#ffffff',
    outline: 'none',
    fontSize: '14px',
    fontFamily: '"Playfair Display", serif, sans-serif',
  },
  icon: {
    opacity: 0.85,
    fontSize: '15px',
    marginRight: '6px',
    whiteSpace: 'nowrap',
  },
  errorText: {
    color: '#ff8a8a',
    fontSize: '13px',
    margin: '0',
    fontWeight: '600',
    textAlign: 'center',
  },
  successText: {
    color: '#a3e4d7',
    fontSize: '13px',
    margin: '0',
    fontWeight: '600',
    textAlign: 'center',
  },
  submitBtn: {
    background: 'linear-gradient(135deg, #D4AF37 0%, #aa8c2c 100%)',
    color: '#513262',
    padding: '16px',
    borderRadius: '30px',
    border: 'none',
    fontWeight: '800',
    fontSize: '16px',
    cursor: 'pointer',
    marginTop: '10px',
    boxShadow: '0 6px 20px rgba(212, 175, 55, 0.35)',
    transition: 'all 0.3s ease',
    width: '100%',
    fontFamily: '"Playfair Display", serif, sans-serif',
  },
  divider: {
    marginTop: '25px',
    fontSize: '12px',
    color: '#f3e5ab',
    textAlign: 'center',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    fontWeight: '600',
  },

  /* SUCCESS POPUP MODAL STYLES */
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(20, 8, 28, 0.78)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
    boxSizing: 'border-box',
    animation: 'authFadeIn 0.3s ease-out forwards',
  },
  successModalCard: {
    background: 'radial-gradient(circle at 50% 0%, #582b6b 0%, #361445 100%)',
    border: '2px solid #D4AF37',
    borderRadius: '26px',
    padding: '38px 28px 32px',
    textAlign: 'center',
    width: '100%',
    maxWidth: '430px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 40px rgba(212, 175, 55, 0.25)',
    color: '#ffffff',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    animation: 'authScaleUp 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
  },
  modalGlow: {
    position: 'absolute',
    top: '-40px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '220px',
    height: '110px',
    background: 'radial-gradient(circle, rgba(212, 175, 55, 0.35) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  iconCircle: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: 'rgba(212, 175, 55, 0.15)',
    border: '2px solid #D4AF37',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    boxShadow: '0 6px 20px rgba(212, 175, 55, 0.3)',
  },
  royalBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(212, 175, 55, 0.15)',
    border: '1px solid rgba(212, 175, 55, 0.45)',
    color: '#fbd349',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    padding: '5px 14px',
    borderRadius: '30px',
    marginBottom: '14px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  modalHeading: {
    fontSize: '25px',
    fontWeight: '800',
    color: '#ffffff',
    margin: '0 0 10px',
    letterSpacing: '0.5px',
    textShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  modalMessage: {
    fontSize: '14px',
    color: '#f3e5ab',
    lineHeight: '1.5',
    margin: '0 0 18px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  redirectNotice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '12.5px',
    color: '#e2f1ec',
    marginBottom: '22px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  modalCtaBtn: {
    background: 'linear-gradient(135deg, #D4AF37 0%, #aa8c2c 100%)',
    color: '#361445',
    border: 'none',
    padding: '14px 22px',
    borderRadius: '30px',
    fontWeight: '800',
    fontSize: '15px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
    transition: 'all 0.25s ease',
    fontFamily: '"Playfair Display", serif, sans-serif',
  },
};

const hoverEffects = `
  .submit-btn-hover:hover {
    background: linear-gradient(135deg, #e2be42, #b89832);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.5);
  }
  @keyframes authFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes authScaleUp {
    from { opacity: 0; transform: scale(0.85) translateY(20px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes pulseGlow {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.2); }
  }
  .auth-pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #10b981;
    display: inline-block;
    animation: pulseGlow 1.4s infinite;
  }
`;

export default AuthPage;